import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  debounce,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MainCard from "../../../../utils/ui-components/MainCard";
import EmptyResult from "../../../../utils/ui-components/EmptyResult";
import Spinner from "../../../../utils/ui-components/Spinner";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import { formatMobile } from "../../../../utils/utils";
import { UserService } from "../../../../assets/_services/user-service";
import { closeConfirmDialog, openConfirmDialog } from "../../../../utils/ui-components/pop-ups/ConfirmDialog";
import { openSuccessDialog } from "../../../../utils/ui-components/pop-ups/SuccessDialog";
import SearchIcon from "@mui/icons-material/Search";
import { gridSpacing } from "../../../../store/constants";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { MENU_OPEN } from "../../../../redux/actions/actions";
import { DataGrid } from "@mui/x-data-grid";
import EmptyResultDataGrid from "../../../../utils/ui-components/EmptyResultDataGrid";
import { useTheme } from "@mui/material/styles";

interface CompanyInterface {
  label: string;
  value: number | string;
}

function ApprovePendingChildUserList() {
  const theme: any = useTheme();

  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoadingApprove, setIsLoadingApprove] = useState({
    userId: "",
    reject: false,
    approve: false,
  });
  const [ownerList, setOwnerList]: any = useState([]);
  const [value, setValue] = useState<CompanyInterface | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [isLoadingSearch, setIsLoadingSearch] = useState(true);
  const path = useLocation();
  const dispatch = useDispatch();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    const id = path?.pathname;
    dispatch({ type: MENU_OPEN, id });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchApprovePendingUserList();
  }, [paginationModel]);

  const fetch = useMemo(
    () =>
      debounce(async (input: string) => {
        let formData = {
          companyName: input,
        };
        if (input.length > 3) {
          setIsLoadingSearch(true);
          const response = await UserService.searchOwnerByCompanyName(formData);
          if (response.isSuccess) {
            setOwnerList(
              response.data.data.map((item: any) => ({
                label: `${item.companyName} (${item.userName})`,
                value: item.companyId,
                id: item.companyId,
              }))
            );
            setIsLoadingSearch(false);
          } else {
            setOwnerList([]);
            setIsLoadingSearch(false);
          }
        } else {
          setOwnerList([]);
          setIsLoadingSearch(false);
        }
      }, 500),
    []
  );

  useEffect(() => {
    // setIsLoadingSearch(true)
    fetch(inputValue);
  }, [inputValue]);

  const fetchApprovePendingUserList = () => {
    setIsLoading(true);
    let formData = {
      companyId: value?.value || "",
    };
    UserService.getAllApprovePendingChildList(formData, paginationModel.page, paginationModel.pageSize).then((response) => {
      if (response.isSuccess) {
        setUsers(response.data.data.rows);
        setCount(response.data.data.count);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });
  };

  const approveOrrejectChild = (id: string, status: number) => {
    closeConfirmDialog();

    setIsLoadingApprove({
      userId: id,
      reject: status === 2 ? true : false,
      approve: status === 1 ? true : false,
    });

    const formData = {
      childUserId: id,
      approveStatus: status,
    };

    UserService?.approveOrRejectChildUser(formData).then((response) => {
      if (response?.isSuccess) {
        openSuccessDialog("Success", response?.data?.comment);
        fetchApprovePendingUserList();
        setIsLoadingApprove({
          userId: "",
          reject: false,
          approve: false,
        });
      } else {
        setIsLoadingApprove({
          userId: "",
          reject: false,
          approve: false,
        });
      }
    });
  };

  const columns = [
    {
      field: "action",
      filterable: false,
      headerName: "",
      sortable: false,
      disableExport: false,
      minWidth: 150,
      maxWidth: 150,
      headerClassName: "stickyActionHeader",
      cellClassName: "stickyColumn",
      renderCell: (params: any) => {
        return (
          <Stack direction="row" justifyContent={"flex-end"}>
            <Tooltip title="Approve user">
              <IconButton
                aria-label="view"
                color="success"
                sx={{ borderRadius: 0, p: 0, mx: 0.3 }}
                onClick={() =>
                  openConfirmDialog(
                    "Approve User",
                    `Are you sure you want to approve this user?`,
                    {
                      action: 0,
                    },
                    () => approveOrrejectChild(params.row?.id, 1)
                  )
                }
                disabled={isLoadingApprove?.userId === params.row?.id && isLoadingApprove?.approve}
              >
                <Typography sx={{ border: "1px solid", py: 0.3, px: 1, borderRadius: 2 }}>
                  {isLoadingApprove?.userId === params.row?.id && isLoadingApprove?.approve && (
                    <CircularProgress color="inherit" sx={{ mr: 1, p: 0, mb: 0 }} size={12} />
                  )}
                  Approve
                </Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title="Reject user">
              <IconButton
                aria-label="view"
                sx={{ borderRadius: 0, p: 0, mx: 0.3 }}
                color="error"
                onClick={() =>
                  openConfirmDialog(
                    "Reject User",
                    `Are you sure you want to Reject this user?`,
                    {
                      action: 0,
                    },
                    () => approveOrrejectChild(params.row?.id, 2)
                  )
                }
                disabled={isLoadingApprove?.userId === params.row?.id && isLoadingApprove?.reject}
              >
                <Typography sx={{ border: "1px solid", py: 0.3, px: 1, borderRadius: 2 }}>
                  {isLoadingApprove?.userId === params.row?.id && isLoadingApprove?.reject && (
                    <CircularProgress color="inherit" sx={{ mr: 1, p: 0, mb: 0 }} size={12} />
                  )}
                  Reject
                </Typography>
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      field: "id",
      filterable: false,
      headerName: "ID",
      sortable: false,
      disableExport: false,
      minWidth: 60,
      maxWidth: 80,
    },
    {
      field: "userName",
      filterable: false,
      headerName: "Username",
      sortable: false,
      disableExport: false,
      minWidth: 150,
      flex: 1,
    },
    {
      field: "representativeName",
      filterable: false,
      headerName: "Representative Name",
      sortable: false,
      disableExport: false,
      minWidth: 150,
      flex: 1,
    },
    {
      field: "email",
      filterable: false,
      headerName: "Email",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "contact",
      filterable: false,
      headerName: "Contact",
      sortable: false,
      disableExport: false,
      minWidth: 150,
      maxWidth: 150,
      flex: 1,
      renderCell: (params: any) => {
        return formatMobile(params.row?.mobile || "");
      },
    },
  ];

  return (
    <Grid container justifyContent="center" spacing={1}>
      <Grid item xs={12}>
        <Grid container spacing={gridSpacing} justifyContent="flex-end">
          <Grid item xl={4} lg={4} md={5} sm={6} xs={9}>
            <Autocomplete
              fullWidth
              filterOptions={(x: any) => x}
              options={ownerList}
              autoComplete
              includeInputInList
              filterSelectedOptions
              value={value}
              noOptionsText="No options"
              onChange={(event, newValue) => {
                setOwnerList(newValue ? [newValue, ...ownerList] : ownerList);
                setValue(newValue);
              }}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Company Name"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {inputValue.length > 3 ? (
                          isLoadingSearch ? (
                            <CircularProgress size={20} />
                          ) : null
                        ) : inputValue.length > 0 ? (
                          isLoadingSearch ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Tooltip title="Search Company">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setIsLoadingSearch(true);
                                  let formData = {
                                    companyName: inputValue,
                                  };
                                  UserService.searchOwnerByCompanyName(formData).then((response) => {
                                    if (response.isSuccess) {
                                      setOwnerList(
                                        response.data.data.map((item: any) => ({
                                          label: `${item.companyName} (${item.userName})`,
                                          value: item.companyId,
                                          id: item.companyId,
                                        }))
                                      );
                                      setIsLoadingSearch(false);
                                    } else {
                                      setOwnerList([]);
                                      setIsLoadingSearch(false);
                                    }
                                  });
                                }}
                              >
                                {/* <ManageSearchIcon fontSize='medium' /> */}
                                <SearchIcon fontSize="medium" />
                              </IconButton>
                            </Tooltip>
                          )
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              // sx={{...theme.typography.customInput}}
              type="button"
              onClick={fetchApprovePendingUserList}
            >
              View
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {/* <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Representative Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell sx={{ minWidth: "170px" }}>Mobile</TableCell>
                <TableCell sx={{ minWidth: "170px" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {users?.length > 0 &&
                !isLoading &&
                users.map((item: any) => (
                  <TableRow hover key={item?.id}>
                    <TableCell>{item?.id}</TableCell>
                    <TableCell>{item?.userName}</TableCell>
                    <TableCell>{item?.representativeName}</TableCell>
                    <TableCell sx={{ minWidth: "200px" }}>{item?.email}</TableCell>
                    <TableCell sx={{ minWidth: "170px" }}>{formatMobile(item?.mobile)}</TableCell>
                    <TableCell sx={{ minWidth: "170px" }} align="right">
                      <Stack direction="row" justifyContent={"flex-end"}>
                        <Tooltip title="Approve user">
                          <IconButton
                            aria-label="view"
                            color="success"
                            sx={{ borderRadius: 0, p: 0, mx: 0.3 }}
                            onClick={() =>
                              openConfirmDialog(
                                "Approve User",
                                `Are you sure you want to approve this user?`,
                                {
                                  action: 0,
                                },
                                () => approveOrrejectChild(item?.id, 1)
                              )
                            }
                            disabled={isLoadingApprove?.userId === item?.id && isLoadingApprove?.approve}
                          >
                            <Typography sx={{ border: "1px solid", py: 0.3, px: 1, borderRadius: 2 }}>
                              {isLoadingApprove?.userId === item?.id && isLoadingApprove?.approve && (
                                <CircularProgress color="inherit" sx={{ mr: 1, p: 0, mb: 0 }} size={12} />
                              )}
                              Approve
                            </Typography>
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject user">
                          <IconButton
                            aria-label="view"
                            sx={{ borderRadius: 0, p: 0, mx: 0.3 }}
                            color="error"
                            onClick={() =>
                              openConfirmDialog(
                                "Reject User",
                                `Are you sure you want to Reject this user?`,
                                {
                                  action: 0,
                                },
                                () => approveOrrejectChild(item?.id, 2)
                              )
                            }
                            disabled={isLoadingApprove?.userId === item?.id && isLoadingApprove?.reject}
                          >
                            <Typography sx={{ border: "1px solid", py: 0.3, px: 1, borderRadius: 2 }}>
                              {isLoadingApprove?.userId === item?.id && isLoadingApprove?.reject && (
                                <CircularProgress color="inherit" sx={{ mr: 1, p: 0, mb: 0 }} size={12} />
                              )}
                              Reject
                            </Typography>
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {!users?.length && !isLoading ? (
            <EmptyResult />
          ) : isLoading ? (
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                minHeight: "40vh",
              }}
            >
              <Spinner />
            </Box>
          ) : null}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 100]}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        </TableContainer> */}
        <Box sx={{ minHeight: 400 }}>
          <DataGrid
            autoHeight
            columns={columns}
            loading={isLoading}
            rows={users}
            slots={{
              noRowsOverlay: EmptyResultDataGrid,
              loadingOverlay: () => <LinearProgress />,
            }}
            style={{ overflow: "auto" }}
            sx={{
              "& .MuiDataGrid-cell.stickyColumn": {
                position: "sticky",
                left: 0,
                backgroundColor: `${theme.palette.mode === "light" ? theme.palette.primary.light : theme.palette.background.paper}`,
                zIndex: 1,
              },
              "& .clicked-row": {
                backgroundColor: `${theme.palette.secondary.light} !important`,
              },
              // cursor: "pointer",
            }}
            pagination
            density="standard"
            getRowId={(row: any) => row.id}
            paginationMode="server"
            rowCount={count}
            disableRowSelectionOnClick
            pageSizeOptions={[5, 10, 25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
          />
        </Box>
      </Grid>
    </Grid>
  );
}

export default ApprovePendingChildUserList;
